import { useId, useState, type FormEvent } from 'react'
import { getAppEnvironment } from '../../lib/env'
import AuthBrand from '../../ui/patterns/AuthBrand'
import { useNavigate, useSearchParams } from 'react-router'
import Button from '../../ui/primitives/Button'
import { getSupabaseClient } from '../../data/supabaseClient'
import { safeRedirectOrDefault } from '../../lib/redirect'

/**
 * Sign in.
 *
 * Deliberately plain. There is no signup link, no "create an organisation", and
 * no password-strength meter: accounts exist only by invitation, and the only
 * thing this page does is exchange a known address and password for a session.
 *
 * The error message is the same whatever went wrong. "No account with that
 * address" is a free account-enumeration oracle, and a person who genuinely
 * mistyped their address is helped just as much by being told the pair did not
 * match.
 */

const GENERIC_ERROR = 'That email address and password do not match. Please try again.'

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const emailId = useId()
  const passwordId = useId()
  const errorId = useId()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Where to go afterwards. Validated here rather than trusted, because it
  // arrived in the query string and anyone can put anything there.
  const destination = safeRedirectOrDefault(searchParams.get('next'))

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) return

    setSubmitting(true)
    setError(null)

    try {
      const { error: signInError } = await getSupabaseClient().auth.signInWithPassword({
        email: email.trim(),
        password,
      })

      if (signInError !== null) {
        // The real reason is never shown. Supabase distinguishes a wrong
        // password from an unknown address, and passing that on would hand a
        // caller a way to test which addresses exist.
        setError(GENERIC_ERROR)
        return
      }

      navigate(destination, { replace: true })
    } catch {
      setError(GENERIC_ERROR)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-4 py-10">
      <div className="rounded-[10px] border border-line bg-surface px-6 py-7">
        <AuthBrand environment={getAppEnvironment()} />
        <h1 className="mt-1 text-[1.6rem] font-bold uppercase tracking-wide text-steel-2">
          Sign in
        </h1>
        <p className="mt-2 text-[0.9rem] text-ink-muted">
          Labour cost and margin intelligence for the workshop.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <label
              htmlFor={emailId}
              className="block text-[0.8125rem] font-medium text-ink-secondary"
            >
              Email address
            </label>
            <input
              id={emailId}
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error === null ? undefined : errorId}
              className="mt-1 w-full rounded-[6px] border border-line bg-surface px-3 py-2 text-[0.95rem] text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber"
            />
          </div>

          <div>
            <label
              htmlFor={passwordId}
              className="block text-[0.8125rem] font-medium text-ink-secondary"
            >
              Password
            </label>
            <input
              id={passwordId}
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error === null ? undefined : errorId}
              className="mt-1 w-full rounded-[6px] border border-line bg-surface px-3 py-2 text-[0.95rem] text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber"
            />
          </div>

          {error !== null && (
            <p
              id={errorId}
              role="alert"
              data-testid="login-error"
              className="rounded-[6px] border border-hazard-ink/30 bg-hazard-wash px-3 py-2 text-[0.875rem] text-hazard-ink"
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" disabled={submitting} className="w-full">
            {submitting ? 'Signing in' : 'Sign in'}
          </Button>
        </form>

        {/*
         * The way to /forgot-password.
         *
         * The page has existed since M5 and nothing has ever linked to it, so
         * somebody who forgot their password could reach it only by typing the
         * URL. Found while trying to send a test email during a live delivery
         * incident: the person who built the page could not find it either.
         */}
        <p className="mt-4 text-[0.8125rem]">
          <button
            type="button"
            data-testid="forgot-password"
            onClick={() => navigate('/forgot-password')}
            className="font-medium text-accent-strong underline underline-offset-2 hover:text-brand-700"
          >
            Forgot your password?
          </button>
        </p>

        <p className="mt-4 text-[0.8rem] leading-relaxed text-ink-muted">
          Access is by invitation. If you do not have an account, ask an owner or administrator at
          your organisation to invite you.
        </p>
      </div>
    </main>
  )
}
